import React, { useState } from 'react';
import { ActivityIndicator, Image, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Text } from '../components/Texte';
import { MaterialCommunityIcons } from '../components/icons';
import { Bell, Camera, ChevronDown, CreditCard, MessageCircleQuestion, Settings, User } from '../components/icons';
import { FondAube } from '../components/FondAube';
import { FondNuit } from '../components/FondNuit';
import { GradientButton } from '../components/GradientButton';
import { useGabarit } from '../hooks/useGabarit';
import { t } from '../i18n';
import { fleche } from '../i18n/sens';
import { LARGEUR_EN_RANGEE } from '../theme/miseEnPage';
import { colors, layout, radius, shadow, spacing, typography } from '../theme/tokens';

export interface StatProfil {
  key: string;
  testID: string;
  /** Couleur de la pastille (fond) ; l'icône dessus est blanche. */
  color: string;
  label: string;
  value: number;
  /** Icône déjà rendue en blanc par l'écran, qui connaît les glyphes de chaque statistique. */
  icon: React.ReactElement;
}

export interface ProfilLargeProps {
  nom: string;
  planLabel: string;
  statut: string;
  avatarUrl?: string | null;
  avatarColor: string;
  avatarTexteColor: string;
  initiales: string;
  occupe: boolean;
  erreurImage: string | null;
  stats: StatProfil[];
  coachingEtat: string;
  coachingAvecHorairesParEmail: boolean;
  onOpenNotifications: () => void;
  onOpenSettings: () => void;
  onChangerPhoto: () => void;
  onChangerStatut: () => void;
  onOpenPersonalInfo: () => void;
  onOpenAbonnement: () => void;
  onOpenCoaching: () => void;
  onOpenHelp: () => void;
}

const AVATAR = 104;
const ECART_CARTES = spacing.lg;
const TRANSITION = { transitionProperty: 'background-color', transitionDuration: '120ms' } as object;

/**
 * Les titres de section du dictionnaire sont écrits en capitales (« MON COACHING ») pour le
 * téléphone. Sur grand écran on les lit comme des titres : première lettre en capitale, le reste en
 * minuscules. Sans effet sur le chinois et l'arabe, qui n'ont pas de casse.
 */
const phrase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase();

interface LigneDef {
  testID: string;
  icone: React.ReactNode;
  titre: string;
  description: string;
  onPress: () => void;
}

/**
 * Ligne de la carte de rubriques : pastille d'icône bleue, titre, description, chevron. Au survol le
 * fond se teinte, comme dans les Paramètres.
 */
function LigneRubrique({ def }: { def: LigneDef }) {
  const [survol, setSurvol] = useState(false);
  const { testID, icone, titre, description, onPress } = def;
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`${titre}. ${description}`}
      onPress={onPress}
      onHoverIn={() => setSurvol(true)}
      onHoverOut={() => setSurvol(false)}
      style={[styles.ligne, survol && styles.ligneSurvol, TRANSITION]}
    >
      <View style={styles.pastille}>{icone}</View>
      <View style={styles.ligneTexte}>
        <Text style={styles.ligneTitre}>{titre}</Text>
        <Text style={styles.ligneDescription}>{description}</Text>
      </View>
      <Text style={styles.chevron}>{fleche('›')}</Text>
    </Pressable>
  );
}

/**
 * Profil sur ordinateur et tablette : même langage que Carrière. Un panneau nuit en tête (qui tu es,
 * ce que tu as fait ce mois-ci), puis les quatre rubriques en cartes. Les actions de l'en-tête
 * (notifications, réglages) restent à droite, comme sur le téléphone.
 *
 * Ordinateur large : identité à gauche, quatre statistiques à droite, quatre cartes en rangée.
 * Fenêtre plus étroite ou tablette : le panneau passe en colonne, les cartes deux par deux.
 */
export function ProfilLarge(p: ProfilLargeProps) {
  const { etendu: gabaritEtendu, largeur } = useGabarit();
  const enRangee = gabaritEtendu && largeur >= LARGEUR_EN_RANGEE;

  const icone = (Icone: typeof User) => <Icone size={22} color={colors.electric} strokeWidth={2} />;
  const lignes: LigneDef[] = [
    {
      testID: 'profile-personal-info',
      icone: icone(User),
      titre: t('profile.personalInfo'),
      description: t('profile.personalInfoSubtitle'),
      onPress: p.onOpenPersonalInfo,
    },
    {
      testID: 'profile-subscription',
      icone: icone(CreditCard),
      titre: t('profile.subscription'),
      description: t('profile.subscriptionSubtitle'),
      onPress: p.onOpenAbonnement,
    },
    {
      testID: 'profile-help',
      icone: icone(MessageCircleQuestion),
      titre: t('profile.feedbackTitle'),
      description: t('profile.feedbackSubtitle'),
      onPress: p.onOpenHelp,
    },
  ];

  const carteLignes = (
    <View testID="profile-liste" style={[styles.bloc, styles.blocListe]}>
      <View style={styles.filet} />
      {lignes.map((l, i) => (
        <React.Fragment key={l.testID}>
          {i > 0 ? <View style={styles.separateur} /> : null}
          <LigneRubrique def={l} />
        </React.Fragment>
      ))}
    </View>
  );

  const carteCoaching = (
    <View testID="profile-coaching-card" style={[styles.bloc, styles.blocCoaching]}>
      <View style={styles.filet} />
      <View style={styles.pastille}>
        <MaterialCommunityIcons name="calendar-check-outline" size={22} color={colors.electric} />
      </View>
      <Text style={styles.coachingTitre}>{phrase(t('profile.coaching'))}</Text>
      <Text testID="profile-coaching-state" style={styles.coachingEtat}>
        {p.coachingEtat}
      </Text>
      {p.coachingAvecHorairesParEmail ? <Text style={styles.coachingHint}>{t('profile.coachingHint')}</Text> : null}
      <View style={styles.coachingPied}>
        <GradientButton label={t('profile.bookSession')} onPress={p.onOpenCoaching} testID="profile-coaching-book" />
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <FondAube />
      <ScrollView
        testID="profile-page-large"
        contentContainerStyle={[styles.content, !enRangee && styles.contentMoyen]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.page}>
          <View style={styles.actions}>
            <Pressable
              testID="profile-notifications"
              accessibilityRole="button"
              accessibilityLabel={t('a11y.notifications')}
              onPress={p.onOpenNotifications}
              hitSlop={8}
              style={styles.iconBtn}
            >
              <Bell size={22} color={colors.grey700} strokeWidth={2} />
            </Pressable>
            <Pressable
              testID="profile-settings"
              accessibilityRole="button"
              accessibilityLabel={t('a11y.settings')}
              onPress={p.onOpenSettings}
              hitSlop={8}
              style={styles.iconBtn}
            >
              <Settings size={22} color={colors.grey700} strokeWidth={2} />
            </Pressable>
          </View>

          <FondNuit style={[styles.panneau, !enRangee && styles.panneauMoyen]} testID="profile-identite">
            <View style={styles.identite}>
              <View style={styles.avatarWrap}>
                {p.avatarUrl ? (
                  <Image testID="profile-avatar-image" source={{ uri: p.avatarUrl }} style={styles.avatar} />
                ) : (
                  <View style={[styles.avatar, { backgroundColor: p.avatarColor }]}>
                    <Text style={[styles.avatarTexte, { color: p.avatarTexteColor }]}>{p.initiales}</Text>
                  </View>
                )}
                {p.occupe ? (
                  <View testID="profile-image-busy" style={styles.occupe}>
                    <ActivityIndicator color={colors.white} />
                  </View>
                ) : null}
                <Pressable
                  testID="profile-avatar-btn"
                  accessibilityRole="button"
                  accessibilityLabel={t('a11y.changePhoto')}
                  onPress={p.onChangerPhoto}
                  hitSlop={8}
                  style={styles.appareil}
                >
                  <Camera size={16} color={colors.white} strokeWidth={2} />
                </Pressable>
              </View>

              <View style={styles.identiteTexte}>
                <View style={styles.nomLigne}>
                  <Text testID="profile-name" style={styles.nom}>
                    {p.nom}
                  </Text>
                  <View testID="profile-plan" style={styles.plan}>
                    <Text style={styles.planTexte}>{p.planLabel}</Text>
                  </View>
                </View>
                <Pressable
                  testID="profile-statut"
                  accessibilityRole="button"
                  accessibilityLabel={t('a11y.changeSearchTarget')}
                  onPress={p.onChangerStatut}
                  hitSlop={6}
                  style={styles.statutLigne}
                >
                  <Text style={styles.statut}>{p.statut}</Text>
                  <ChevronDown size={16} color={colors.blancAtenue} strokeWidth={2} />
                </Pressable>
                {p.erreurImage ? (
                  <Text testID="profile-image-error" style={styles.erreur}>
                    {p.erreurImage}
                  </Text>
                ) : null}
              </View>
            </View>

            <View style={styles.activite}>
              <Text style={styles.activiteTitre}>{phrase(t('profile.activity'))}</Text>
              <View style={styles.statsLigne}>
                {p.stats.map((s) => (
                  <View key={s.key} testID={s.testID} style={[styles.stat, enRangee ? styles.statRangee : styles.statMoyen]}>
                    <View style={[styles.statPastille, { backgroundColor: s.color }]}>{s.icon}</View>
                    <Text style={styles.statNombre}>{s.value}</Text>
                    <Text style={styles.statLibelle}>{s.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </FondNuit>

          <View testID="profile-rubriques" style={enRangee ? styles.rangee : styles.pile}>
            {carteLignes}
            {carteCoaching}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.xxl + spacing.xl,
  },
  contentMoyen: { paddingHorizontal: spacing.xxl },
  page: { width: '100%', maxWidth: layout.pageMaxWidth, alignSelf: 'center', gap: spacing.lg },

  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: spacing.xs },
  iconBtn: { width: 40, height: 40, borderRadius: radius.pill, alignItems: 'center', justifyContent: 'center' },

  panneau: {
    borderRadius: radius.xl,
    padding: spacing.xl + spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.xxl,
    overflow: 'hidden',
  },
  panneauMoyen: { flexDirection: 'column', alignItems: 'stretch', gap: spacing.xl },

  identite: { flexDirection: 'row', alignItems: 'center', gap: spacing.xl, flexShrink: 1 },
  avatarWrap: { width: AVATAR, height: AVATAR },
  avatar: {
    width: AVATAR,
    height: AVATAR,
    borderRadius: radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 3,
    borderColor: 'rgba(255, 255, 255, 0.85)',
  },
  avatarTexte: { fontSize: typography.display.size, fontWeight: '700' },
  occupe: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: radius.pill,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  appareil: {
    position: 'absolute',
    end: -2,
    bottom: 2,
    width: 32,
    height: 32,
    borderRadius: radius.pill,
    backgroundColor: colors.electric,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.navy,
  },
  identiteTexte: { gap: spacing.xs, flexShrink: 1 },
  nomLigne: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: spacing.sm },
  nom: { fontSize: typography.h1.size + spacing.sm, lineHeight: typography.h1.lineHeight + spacing.sm, fontWeight: '800', color: colors.white, letterSpacing: -0.5 },
  plan: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
    borderRadius: radius.pill,
    backgroundColor: colors.surfaceNuitForte,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
  },
  planTexte: { fontSize: typography.small.size, fontWeight: '700', color: colors.white },
  statutLigne: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, alignSelf: 'flex-start' },
  statut: { fontSize: typography.body.size, color: colors.blancAtenue },
  erreur: { fontSize: typography.small.size, color: colors.corail },

  activite: { gap: spacing.sm },
  activiteTitre: { fontSize: typography.small.size, fontWeight: '700', color: colors.blancAtenue },
  statsLigne: { flexDirection: 'row', gap: spacing.md },
  stat: {
    padding: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.surfaceNuit,
    borderWidth: 1,
    borderColor: colors.bordureNuit,
    gap: spacing.xs,
  },
  statRangee: { width: 124 },
  statMoyen: { flex: 1 },
  statPastille: { width: 32, height: 32, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center', marginBottom: spacing.xs },
  statNombre: { fontSize: 30, lineHeight: 34, fontWeight: '800', color: colors.white, letterSpacing: -1 },
  statLibelle: { fontSize: typography.small.size, color: colors.blancAtenue },

  rangee: { flexDirection: 'row', alignItems: 'stretch', gap: ECART_CARTES },
  pile: { flexDirection: 'column', gap: ECART_CARTES },

  bloc: {
    borderRadius: radius.xl,
    backgroundColor: colors.white,
    overflow: 'hidden',
    ...shadow.card,
  },
  blocListe: { flex: 1.25, paddingTop: spacing.lg, paddingBottom: spacing.sm, paddingHorizontal: spacing.sm, justifyContent: 'center' },
  blocCoaching: { flex: 1, padding: spacing.xl, paddingTop: spacing.xl + spacing.sm, gap: spacing.sm },
  filet: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, backgroundColor: colors.electric },

  ligne: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderRadius: radius.lg,
    cursor: 'pointer',
  },
  ligneSurvol: { backgroundColor: colors.grey50 },
  pastille: {
    width: 44,
    height: 44,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.blueSoft,
  },
  ligneTexte: { flex: 1, gap: 2, minWidth: 0 },
  ligneTitre: { fontSize: typography.bodyMd.size, fontWeight: '700', color: colors.navy },
  ligneDescription: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
  chevron: { fontSize: typography.h3.size, color: colors.grey400 },
  separateur: { height: 1, backgroundColor: colors.grey100, marginHorizontal: spacing.lg },

  coachingTitre: { fontSize: typography.h1.size - 2, lineHeight: typography.h1.lineHeight, fontWeight: '800', color: colors.navy, marginTop: spacing.xs },
  coachingEtat: { fontSize: typography.bodyMd.size, lineHeight: typography.body.lineHeight, color: colors.grey700 },
  coachingHint: { fontSize: typography.small.size, color: colors.grey500 },
  coachingPied: { marginTop: 'auto', paddingTop: spacing.md },
});
